// A common filter component, that expects a "filter type - date/event_type..", and filter options, and a callback to update the filter value in the parent component.
import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from './DashboardFilters.module.css';
import DownIcon from "../../../../assets/DownIcon";

type FilterProps = {
  label?: string;
  value: string;
  options: Array<string | { label: string; value: string }>;
  onChange: (value: string) => void;
  style?: React.CSSProperties;
  disabled?: boolean;
  selectName?: string;
  /** When true, option labels are shown as provided (e.g. uppercase Adserver types). */
  preserveOptionCase?: boolean;
};

const formatDisplayLabel = (text: string) =>
  text
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const DashboardFilters: React.FC<FilterProps> = ({
  label,
  value,
  options,
  onChange,
  style,
  disabled,
  selectName = "Select",
  preserveOptionCase = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isAllExplicitlySelected, setIsAllExplicitlySelected] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const normalizedOptions = useMemo(
    () => options.map((option) =>
      typeof option === "string" ? { label: option, value: option } : option,
    ),
    [options],
  );
  const hasAllOption = normalizedOptions.some((option) => option.value === "ALL");
  const selectedValue = value === "" && hasAllOption && isAllExplicitlySelected ? "ALL" : value;
  const selectedOption = normalizedOptions.find((option) => option.value === selectedValue);
  const displayOptionLabel = (optionLabel: string) =>
    preserveOptionCase ? optionLabel : formatDisplayLabel(optionLabel);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleOptionSelect = (selectedValue: string) => {
    setIsAllExplicitlySelected(selectedValue === "ALL");
    onChange(selectedValue);
    setIsOpen(false);
  };

  return (
    <div className={styles.filterContainer} ref={dropdownRef}>
      {label && <label className={styles.label}>{formatDisplayLabel(label)}</label>}
      <button
        type="button"
        disabled={disabled}
        style={style}
        className={styles.filterSelect}
        onClick={() => setIsOpen((prevOpen) => !prevOpen)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        {selectedOption
          ? displayOptionLabel(selectedOption.label)
          : formatDisplayLabel(selectName)}
      </button>
      {isOpen && !disabled && (
        <div className={styles.optionContainer}>
          <ul className={styles.options} role="listbox">
        {normalizedOptions.map((option) => {
          const isSelected = option.value === selectedValue;
          return (
                <li key={option.value} role="option" aria-selected={isSelected}>
                  <button
                    type="button"
                    className={`${styles.option} ${isSelected ? styles.optionSelected : ""}`}
                    onClick={() => handleOptionSelect(option.value)}
                  >
                    {displayOptionLabel(option.label)}
                  </button>
                </li>
          );
        })}
          </ul>
        </div>
      )}
      <DownIcon className={styles.downIcon} width={10} height={6} style={{ color: '#092538', flexShrink: 0, marginTop: 3 }} />
    </div>
  );
};

export default DashboardFilters;